/** Which UI language to start in.
 *
 *  Order: ``?lng=`` (support reproduction, never persisted), then the
 *  stored explicit choice, then the browser's languages through the
 *  registry aliases, then English. Nothing here writes storage. */
import { languages as registryLanguages, resolveCode, type LanguageEntry } from "./languages";
import { readStoredUiLanguage } from "./persistence";

/** The pseudo-locale: accented, padded English for spotting unwrapped
 *  strings and clipped layouts. Dev builds and VITE_I18N_PSEUDO=1 only;
 *  never in the registry or the picker. Reach it with ``?lng=en-XA``. */
export const PSEUDO = "en-XA";
export const PSEUDO_ENABLED: boolean =
  import.meta.env.DEV || import.meta.env.VITE_I18N_PSEUDO === "1";

export type DetectionSource = "query" | "stored" | "navigator" | "default";

export interface Detection {
  code: string;
  source: DetectionSource;
}

export interface DetectEnv {
  /** ``location.search``. */
  search?: string;
  /** The stored explicit choice (``repoworks.uiLanguage``). */
  stored?: string | null;
  /** ``navigator.languages``, most preferred first. */
  navigatorLanguages?: readonly string[];
  /** The registry entries (tests inject a fixture). */
  languages?: readonly LanguageEntry[];
  pseudo?: boolean;
}

function browserEnv(): DetectEnv {
  const nav = typeof navigator === "undefined" ? undefined : navigator;
  const navigatorLanguages =
    nav?.languages && nav.languages.length > 0
      ? nav.languages
      : nav?.language
        ? [nav.language]
        : [];
  return {
    search: typeof window === "undefined" ? "" : window.location.search,
    stored: readStoredUiLanguage(),
    navigatorLanguages,
  };
}

export function detectUiLanguage(env: DetectEnv = browserEnv()): Detection {
  const list = env.languages ?? registryLanguages;
  const pseudo = env.pseudo ?? PSEUDO_ENABLED;
  const ui = new Set(list.filter((l) => l.ui).map((l) => l.code));

  const query = new URLSearchParams(env.search ?? "").get("lng");
  if (query) {
    if (pseudo && query.toLowerCase() === PSEUDO.toLowerCase()) {
      return { code: PSEUDO, source: "query" };
    }
    const code = resolveCode(query, ui, list);
    if (code) return { code, source: "query" };
  }

  if (env.stored && ui.has(env.stored)) return { code: env.stored, source: "stored" };

  for (const tag of env.navigatorLanguages ?? []) {
    const code = resolveCode(tag, ui, list);
    if (code) return { code, source: "navigator" };
  }

  return { code: "en", source: "default" };
}
